/**
 * The navigation. There is one of these, and it is the only one.
 *
 * The previous app rendered this sidebar and then let the radar mount a second
 * sidebar inside the content area, so /wre showed two navigations, two brand
 * marks and two notification bells — and the radar's own screens were state
 * flags rather than routes, so they had no address and the back button skipped
 * past them. Every destination below is a real route.
 *
 * The chrome is Airbnb's: white surface, hairline divider, pill rows that fill
 * grey on hover, and category headings that are small and grey rather than
 * another band of colour.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Bot, Building2, ChevronDown, FlaskConical, LayoutDashboard, LineChart, LogOut, MapPin, MessageCircleMore,
  MessageSquare, Menu, Radar, Satellite, Settings, ShieldCheck, Users, X,
} from "lucide-react";
import { useSession } from "@/lib/session";
import Mark from "@/components/ui/Mark";
import { IconButton, cx } from "@/components/ui/Button";

interface Item {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** A waiting count. Rendered only when it is above zero. */
  count?: number;
}

interface Group {
  heading?: string;
  items: Item[];
}

export const NAV: Group[] = [
  { items: [
    { to: "/home",     label: "היום",      icon: LayoutDashboard },
    { to: "/radar",    label: "ראדאר",     icon: Radar },
    { to: "/inbox",    label: "שיחות",     icon: MessageSquare },
    { to: "/contacts", label: "אנשי קשר",  icon: Users },
  ]},
  { heading: "מעקב", items: [
    { to: "/simulator", label: "סימולטור",      icon: FlaskConical },
    { to: "/scans",     label: "סריקות",        icon: Satellite },
    { to: "/analytics", label: "נתונים ולוגים", icon: LineChart },
  ]},
  { heading: "הגדרות", items: [
    { to: "/sources",  label: "מקורות",        icon: Building2 },
    { to: "/agent",    label: "הסוכן",         icon: Bot },
    { to: "/channel",  label: "וואטסאפ",       icon: MessageCircleMore },
    { to: "/geometry", label: "בדיקת כתובות",  icon: MapPin },
    { to: "/settings", label: "הגדרות",        icon: Settings },
  ]},
];

const OPEN_KEY = "bootwhat.rail.groups";

/**
 * Which named groups the operator has explicitly opened or closed.
 *
 * A heading that is ABSENT is not "closed" — it means the operator has never
 * touched it, and the group falls back to opening only when it holds the
 * current screen. That distinction is the whole behaviour: the rail starts
 * quiet, shows you where you are, and once you make a choice it keeps it.
 */
function readOpenGroups(): Record<string, boolean> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(OPEN_KEY) ?? "null");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    // Private browsing, or a value someone else wrote. The default is fine.
    return {};
  }
}

/** Does this group contain the screen we are on? Detail routes count as their parent. */
function holdsPath(group: Group, pathname: string): boolean {
  return group.items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
}

export default function Rail({
  open, onClose, counts,
}: {
  open: boolean;
  onClose: () => void;
  counts?: Record<string, number>;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { profile, business, connected } = useSession();
  const [openGroups, setOpenGroups] = useState(readOpenGroups);

  // Platform admin is a group like any other rather than a block of its own, so
  // it collapses, remembers and counts by the same code as the rest.
  const groups = useMemo<Group[]>(() => (
    profile?.isAdmin
      ? [...NAV, { heading: "פלטפורמה", items: [{ to: "/admin", label: "ניהול פלטפורמה", icon: ShieldCheck }] }]
      : NAV
  ), [profile?.isAdmin]);

  // Navigating on mobile means the drawer has done its job.
  useEffect(() => { onClose(); }, [pathname, onClose]);

  useEffect(() => {
    try { window.localStorage.setItem(OPEN_KEY, JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups]);

  /**
   * Landing inside a closed group opens it.
   *
   * Without this, a link from anywhere else in the app — a card, a redirect,
   * a bookmark — leaves the operator on a screen whose row in the rail is
   * folded away, which reads as "this screen is not in the menu".
   */
  useEffect(() => {
    const active = groups.find((g) => g.heading && holdsPath(g, pathname));
    const heading = active?.heading;
    if (!heading) return;
    setOpenGroups((prev) => (prev[heading] ? prev : { ...prev, [heading]: true }));
  }, [pathname, groups]);

  const logout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
    navigate("/login");
  };

  return (
    <aside className={cx("rail", open && "is-open")} aria-label="ניווט ראשי">
      <div className="rail-head">
        <Link to="/home" className="brand">
          <span className="brand-mark"><Mark size={26} /></span>
          <span>
            <b>KEYLIV</b>
            <small>{business?.name ?? "תיווך שמתקדם איתך"}</small>
          </span>
        </Link>
        <span className="rail-close">
          <IconButton label="סגירת תפריט" onClick={onClose}><X /></IconButton>
        </span>
      </div>

      <nav className="rail-nav">
        {groups.map((group, index) => {
          // `end` on /home only: every other route is a leaf, so a prefix
          // match would light two rows at once on a detail page.
          const links = group.items.map(({ to, label, icon: Icon }) => {
            const count = counts?.[to];
            return (
              <NavLink key={to} to={to} end={to === "/home"} className={({ isActive }) => cx("rail-link", isActive && "is-active")}>
                <Icon />
                <span>{label}</span>
                {count ? <span className="rail-count">{count > 99 ? "99+" : count}</span> : null}
              </NavLink>
            );
          });

          // The first group has no heading, so there is nothing to click and
          // nothing to fold — the four screens the operator lives in stay put.
          if (!group.heading) return <div className="rail-group" key={index}>{links}</div>;

          const heading = group.heading;
          const isOpen = openGroups[heading] ?? holdsPath(group, pathname);
          // A folded group must not swallow the reason to open it, so anything
          // waiting inside surfaces on the header instead.
          const waiting = group.items.reduce((n, i) => n + (counts?.[i.to] ?? 0), 0);
          const panelId = `rail-panel-${index}`;

          return (
            <div className={cx("rail-group", isOpen && "is-open")} key={heading}>
              <button
                type="button"
                className="rail-toggle"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenGroups((prev) => ({ ...prev, [heading]: !isOpen }))}
              >
                <ChevronDown className="rail-chevron" />
                <span>{heading}</span>
                {!isOpen && waiting > 0 && (
                  <span className="rail-count is-hot">{waiting > 99 ? "99+" : waiting}</span>
                )}
              </button>
              {/* The row count drives the fold's height cap — see shell.css. */}
              <div className="rail-panel" id={panelId} style={{ "--rail-rows": group.items.length } as React.CSSProperties}>
                {links}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="rail-foot">
        {/* A link, not an indicator: "disconnected" has exactly one answer, and
            a dead-end status light just makes the operator hunt for it. */}
        <Link to="/channel" className={cx("rail-status", connected && "is-live")}>
          <i />
          <div>
            <b>{connected ? "וואטסאפ פעיל" : "וואטסאפ מנותק"}</b>
            <small>{connected ? "הקליטה מהקבוצות רצה" : "נדרשת סריקת QR"}</small>
          </div>
        </Link>
        <div className="rail-actions">
          <Link to="/settings"><Settings />הגדרות</Link>
          <button type="button" onClick={logout}><LogOut />יציאה</button>
        </div>
      </div>
    </aside>
  );
}

/** The mobile header. Only rendered under the shell's breakpoint. */
export function TopBar({ onOpen }: { onOpen: () => void }) {
  const { business } = useSession();
  return (
    <header className="topbar">
      <Link to="/home" className="brand">
        <span className="brand-mark"><Mark size={22} /></span>
        <b>{business?.name ?? "KEYLIV"}</b>
      </Link>
      <IconButton label="פתיחת תפריט" onClick={onOpen}><Menu /></IconButton>
    </header>
  );
}
